import { EmailStatus } from '@models/portfolioadmin/reportManagement/emailStatus';
import { SendRecipient, SendRecipients } from '@models/portfolioadmin/reportManagement/reportDetails';
import { UserItem } from '@app/users/UserItem';

interface SelectableReport {
  reportId: number;
  reportName: string;
  reportDate: string;
  isCustodian: boolean;
  approved: boolean;
  isLocked?: boolean;
  emailStatuses?: Array<{ id: number; status: EmailStatus; emailAddress: string }>;
  selected: boolean;
  disabled: boolean;
}

class EmailReportsDialogController implements angular.IOnInit {
  public generatedReports: SelectableReport[] = [];
  public custodianReports: SelectableReport[] = [];
  public reportDate: string = '';
  public sendRecipients: SendRecipients | null = null;
  public users: UserItem[] = [];
  public isLoadingUsers: boolean = false;
  public isSending: boolean = false;

  public toRecipients: SendRecipient[] = [];
  public ccRecipients: SendRecipient[] = [];
  private allRecipients: SendRecipient[] = [];
  public toSearchText: string = '';
  public ccSearchText: string = '';

  public onSend: (params: {
    clientReportIds: number[];
    custodianReportIds: number[];
    recipientUserIds: number[];
    recipientCcUserIds?: number[];
  }) => Promise<any>;

  /** @ngInject */
  constructor(
    private $mdDialog: angular.material.IDialogService,
    private notifyjs: any
  ) {}

  $onInit(): void {
    this.initRecipients();
  }

  $onChanges(changes: angular.IOnChangesObject): void {
    if ((changes['sendRecipients'] && this.sendRecipients) || (changes['users'] && this.users)) {
      this.initRecipients();
    }
  }

  private initRecipients(): void {
    const recipients = (this.sendRecipients && this.sendRecipients.recipients) || [];
    const defaultCc = (this.sendRecipients && this.sendRecipients.defaultCcRecipients) || [];
    const users = this.users || [];

    const byKey = new Map<string, SendRecipient>();
    [...recipients, ...defaultCc].forEach(r => {
      if (!r || (!r.id && !r.email)) return;
      const key = (r.id != null ? String(r.id) : (r.email || '').toLowerCase()) || `_${r.email}`;
      if (!byKey.has(key)) byKey.set(key, { id: r.id, email: r.email, name: r.name || r.email });
    });
    users.forEach(u => {
      if (!u || !u.email) return;
      const key = (u.id != null ? String(u.id) : u.email.toLowerCase()) || `_${u.email}`;
      if (!byKey.has(key)) byKey.set(key, { id: u.id, email: u.email, name: u.name || u.email });
    });
    this.allRecipients = Array.from(byKey.values());

    const reportLinkIds = new Set(recipients.map(r => r.id).filter((id): id is number => id != null));
    this.toRecipients = this.allRecipients.filter(r => r.id != null && reportLinkIds.has(r.id));

    const defaultCcKeys = new Set(defaultCc.map(d => (d.id != null ? String(d.id) : (d.email || '').toLowerCase())));
    this.ccRecipients = this.allRecipients.filter(r =>
      (r.id != null && defaultCcKeys.has(String(r.id))) ||
      (r.email && defaultCcKeys.has((r.email || '').toLowerCase()))
    );
  }

  /** Options for md-select - selected items shown first */
  private getOptionsWithSelectedFirst(selected: SendRecipient[]): SendRecipient[] {
    const selectedIds = new Set((selected || []).map(r => r.id).filter((id): id is number => id != null));
    const selectedEmails = new Set(
      (selected || []).map(r => (r.email || '').toLowerCase()).filter(Boolean)
    );
    const isSelected = (r: SendRecipient) =>
      (r.id != null && selectedIds.has(r.id)) ||
      (r.email && selectedEmails.has((r.email || '').toLowerCase()));
    const sel: SendRecipient[] = [];
    const unsel: SendRecipient[] = [];
    (this.allRecipients || []).forEach(r => (isSelected(r) ? sel : unsel).push(r));
    return [...sel, ...unsel];
  }

  public getToOptionsForSelect = () => this.getOptionsWithSelectedFirst(this.toRecipients);
  public getCcOptionsForSelect = () => this.getOptionsWithSelectedFirst(this.ccRecipients);

  private getSelectedText(list: SendRecipient[]): string {
    return (list || []).map(r => r.name || r.email || '').filter(Boolean).join(', ');
  }
  public getToSelectedText = () => this.getSelectedText(this.toRecipients);
  public getCcSelectedText = () => this.getSelectedText(this.ccRecipients);

  public clearToSearch = () => { this.toSearchText = ''; };
  public clearCcSearch = () => { this.ccSearchText = ''; };

  public getSelectedCount(): number {
    const allReports = [...this.generatedReports, ...this.custodianReports];
    return allReports.filter(r => r.selected && !r.disabled).length;
  }

  public displayStatus(report: SelectableReport): string {
    if (!report.emailStatuses || report.emailStatuses.length === 0) {
      return 'Not Sent';
    }
    const statuses = report.emailStatuses;
    const latestStatus = statuses[statuses.length - 1].status;
    switch (latestStatus) {
      case EmailStatus.PENDING:
        return 'Not Sent';
      case EmailStatus.SENDING:
        return 'Sending';
      case EmailStatus.SENT:
        return 'Sent';
      case EmailStatus.OPENED:
        return 'Opened';
      case EmailStatus.FAILED:
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  public sendEmail(): void {
    const selectedIds = (reports: SelectableReport[]) => reports
      .filter(r => r.selected && !r.disabled)
      .map(r => r.reportId);
    const clientReportIds = selectedIds(this.generatedReports);
    const custodianReportIds = selectedIds(this.custodianReports);

    if (clientReportIds.length === 0 && custodianReportIds.length === 0) {
      this.notifyjs('Please select at least one report to send.', 'warning');
      return;
    }

    const recipientUserIds = (this.toRecipients || [])
      .filter(r => r.id != null)
      .map(r => r.id as number);
    const recipientCcUserIds = (this.ccRecipients || [])
      .filter(r => r.id != null)
      .map(r => r.id as number);

    const toWithoutId = (this.toRecipients || []).filter(r => r.id == null);
    if (toWithoutId.length > 0) {
      this.notifyjs('Some To recipients do not have a valid user ID. Please remove and re-add them.', 'warning');
      return;
    }
    if (recipientUserIds.length === 0) {
      this.notifyjs('Please select at least one To recipient.', 'warning');
      return;
    }

    this.isSending = true;

    if (this.onSend) {
      this.onSend({
        clientReportIds,
        custodianReportIds,
        recipientUserIds,
        recipientCcUserIds: recipientCcUserIds.length > 0 ? recipientCcUserIds : undefined
      })
        .then(() => this.$mdDialog.hide(true))
        .catch(err => {
          this.isSending = false;
          const errorMessage = err.data || err.message || 'Failed to send reports. Please try again.';
          this.notifyjs(errorMessage, 'error');
        });
    } else {
      this.isSending = false;
      this.notifyjs('Send function not configured.', 'error');
    }
  }

  public cancel(): void {
    this.$mdDialog.cancel();
  }
}

export const emailReportsDialog: angular.IComponentOptions = {
  controller: EmailReportsDialogController,
  template: require('./emailReports.dialog.html'),
  bindings: {
    generatedReports: '<',
    custodianReports: '<',
    reportDate: '<',
    sendRecipients: '<',
    users: '<',
    isLoadingUsers: '<',
    onSend: '&'
  }
};



// WEBPACK FOOTER //
// ./src/app/portfolioadmin/reportManagement/emailReportsDialog/emailReports.dialog.ts